/* eslint-disable no-restricted-imports */
import axios from "axios";
import { Logger } from "@/core/utilities";
import { useFileDownloadTracker } from "@/shared/gtm/download-counter";
import { getFileExtension } from "../../shared";
import type { ICertificateInfo } from "./types";
import type { Compliance } from "@/core/api/graphql/types";
import type { AxiosRequestConfig } from "axios";

export const REACH_CERTIFICATE_TYPE = "REACH";
export const ROHS_CERTIFICATE_TYPE = "RoHS";
export const TSCA_CERTIFICATE_TYPE = "TSCA";
export const ROHS_NOT_APPLICABLE = "Not Applicable";
export const ASSET_GROUP_SDS = "SDS";
export const ASSET_GROUP_TSCA = "TSCA";
export const ASSET_GROUP_DSL = "DSL";
export const ASSET_GROUP_CATALOG_PRESENTATION = "Catalog Presentation";
export const ROHS_COMPLIANT = "Compliant";
export const TSCA_COMPLIANT = "Compliant";

export const rohsDoNotGenerate = ["Awaiting Information", "EU 2015/863", "Non-Compliant", "Not Applicable"];
export const reachDoNotGenerate = ["Awaiting Information", "Do Not Migrate", "Not Applicable"];
export const tscaDoNotGenerate: string[] = [];

export const getWebsiteByUserCurrency = (currencyCode: string): string => {
  switch (currencyCode.toUpperCase()) {
    case "USD":
      return "www.thorlabs.com";
    case "EUR":
      return "www.thorlabs.com";
    case "GBP":
      return "www.thorlabs.com";
    case "SEK":
      return "www.thorlabs.com";
    case "CNY":
      return "www.thorlabschina.cn";
    case "JPY":
      return "www.thorlabs.co.jp";
    case "BRL":
      return "www.thorlabs.com";
    default:
      return "www.thorlabs.com";
  }
};

export const getCertificationInfo = (compliances: Compliance[], doNotGenerateValues: string[]): ICertificateInfo => {
  const result: ICertificateInfo = {
    hasCertificate: false,
    shouldGenerateCertificate: false,
    values: [],
  };

  if (compliances.length > 0) {
    result.hasCertificate = true;
  } else {
    result.hasCertificate = false;
    return result;
  }

  //Check if any compliance value has a do not generate value
  const doNotGenerateValue = compliances.find((certificate) =>
    doNotGenerateValues.map((value) => value.toLowerCase()).includes(certificate.displayText?.toLowerCase() ?? ""),
  );

  if (doNotGenerateValue) {
    result.values.push(doNotGenerateValue.displayText);
    return result;
  }

  result.shouldGenerateCertificate = true;
  compliances.forEach((certificate) => {
    result.values.push(certificate.displayText);
  });

  return result;
};

export const openFile = (fileUrl: string, fileName: string = "") => {
  const { gtmTrackFileDownload, isDownloadableFile } = useFileDownloadTracker();
  if (isDownloadableFile(fileName, true)) {
    gtmTrackFileDownload(fileName, true);
  }

  window.open(fileUrl, "_blank");
};

export const openFileByUrl = (url: string, fileName: string = "") => {
  const { gtmTrackFileDownload, isDownloadableFile } = useFileDownloadTracker();
  if (isDownloadableFile(fileName, true)) {
    gtmTrackFileDownload(fileName, true);
  }

  window.open(url, "_blank");
};

export const downloadFile = async (
  url: string,
  fileName: string,
  axiosConfig: AxiosRequestConfig | undefined = undefined,
  blobProperties: BlobPropertyBag | undefined = undefined,
) => {
  try {
    const response = await axios.get(url, axiosConfig);
    const blob = new Blob([response.data], blobProperties);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } catch (error) {
    Logger.error("Error downloading the file", error);
  }
};

export const normalizeDownloadFileName = (
  name: string,
  type: string,
  description: string,
  productCode: string,
  hyphenateFullName = false,
): string => {
  let fileName = description?.trim() ? description : type;

  if (fileName.trim() === "") {
    return `${productCode}-File.${getFileExtension(name)}`;
  }

  fileName = hyphenateFullName
    ? `${productCode}-` +
      fileName
        .replace(/ \/ /g, "-")
        .replace(/[(),']/g, "")
        .replace(/\s+/g, "-")
    : `${productCode}-` +
      fileName
        .replace(" / ", "-")
        .replace("(", "")
        .replace(")", "")
        .replace("'", "")
        .replace(",", "")
        .replace(" ", "-");

  return `${fileName}.${getFileExtension(name)}`;
};

export const getDocumentIconName = (group: string) => {
  if (!group) {
    return "";
  }

  const lowerCaseGroup = group.toLowerCase();

  if (lowerCaseGroup.includes("pdf")) {
    return "icon-cad";
  } else if (lowerCaseGroup.includes("solidworks")) {
    return "icon-solidworks";
  } else if (lowerCaseGroup.includes("edrawing")) {
    return "icon-box";
  } else if (lowerCaseGroup.includes("manual")) {
    return "icon-manual";
  } else {
    return "icon-document";
  }
};
